/**
 * Environment configuration
 *
 * All environment variables should be accessed through this module.
 * This provides type safety and a single source of truth.
 *
 * Vite environment variables:
 * - Must be prefixed with VITE_ to be exposed to client code
 * - Access via import.meta.env.VITE_VARIABLE_NAME
 * - Define in .env, .env.local, .env.development, .env.production files
 */

import { parseFeatureFlags, buildFeatures } from "@/feature-flags/feature-flags";
import { parseHostOverrides, resolveHostProfile } from "@/config/host-overrides";
import type { HostProfile } from "@/config/host-overrides";

const REDIRECTION_ALLOWED_DOMAINS = (import.meta.env.VITE_REDIRECTION_ALLOWED_DOMAINS ?? "")
  .split(",")
  .map((domain: string) => domain.trim())
  .filter(Boolean);

const FEATURES = buildFeatures(parseFeatureFlags(import.meta.env.VITE_FEATURE_MANAGEMENT));

const HOST_PROFILE_DEFAULTS: HostProfile = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? "/api",
  defaultReturnUrl: import.meta.env.VITE_REDIRECTION_DEFAULT_RETURNURL ?? "",
};

/**
 * Resolves the host profile once, for the current hostname.
 *
 * A malformed `VITE_HOST_OVERRIDES` value must not stop sign-in. The build
 * step refuses a malformed value before deploy, so this catch guards only
 * an unbuilt or misconfigured run.
 */
function resolveHostProfileForThisBuild(): HostProfile {
  try {
    const overrides = parseHostOverrides(import.meta.env.VITE_HOST_OVERRIDES);
    return resolveHostProfile(window.location.hostname, overrides, HOST_PROFILE_DEFAULTS);
  } catch (error) {
    console.error(
      "VITE_HOST_OVERRIDES is malformed. The app keeps the default host profile.",
      error
    );
    return HOST_PROFILE_DEFAULTS;
  }
}

const HOST_PROFILE = resolveHostProfileForThisBuild();

interface EnvConfig {
  /** Base URL for the API endpoints */
  apiBaseUrl: string;
  /** Origin of `apiBaseUrl`; a relative base resolves to this page's origin */
  apiOrigin: string;
  /** Google reCAPTCHA v3 site key */
  recaptchaSiteKey: string;
  /** Allowed domains for redirect URL validation (supports subdomain matching with leading dot) */
  allowedRedirectDomains: string[];
  /** Default redirect URL when no returnUrl is stored (empty string = fall back to /home) */
  redirectDefaultUrl: string;
  /** Feature flags */
  features: {
    emailLogin: boolean;
    kretaLogin: boolean;
    googleLogin: boolean;
    elteLogin: boolean;
    register: boolean;
    /** MTP tenant picker + realm routing (A051). Off ⇒ exact DPA login flow. */
    tenantPicker: boolean;
  };
}

export const env: EnvConfig = {
  apiBaseUrl: HOST_PROFILE.apiBaseUrl,
  apiOrigin: new URL(HOST_PROFILE.apiBaseUrl, window.location.origin).origin,
  recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? "",
  allowedRedirectDomains: REDIRECTION_ALLOWED_DOMAINS,
  redirectDefaultUrl: HOST_PROFILE.defaultReturnUrl,
  features: FEATURES,
};
